import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import SearchIcon from "@mui/icons-material/Search";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import {
  Box,
  Button,
  createTheme,
  InputAdornment,
  styled,
  TextField,
  ThemeProvider,
  Tooltip,
  Typography,
  Zoom,
} from "@mui/material";
import { DataGrid, GridFooter, GridFooterContainer } from "@mui/x-data-grid";
import { useGetPlatformsQuery } from "state/rtkQuery/platform/platformQueries";
import {
  useTriggerOrgUserSyncMutation,
  useUpdateUserOrgStatusBatchMutation,
} from "state/rtkQuery/user/userMutations";
import {
  useGetOrgUserSyncStatusQuery,
  useGetUsersByOrgAndStatusQuery,
} from "state/rtkQuery/user/userQueries";
import { userServiceApiSlice } from "state/services/userServiceApiSlice";

import LoadingButton from "components/LoadingButton/LoadingButton";

import BulkChangeBudgetModal from "../JoinedTab/BulkChangeBudgetModal";

import LargeDataPagination from "./LargeDataPagination/LargeDataPagination";
import SyncUsersConfirmationModal from "./SyncUsersConfirmationModal/SyncUsersConfirmationModal";
import ReusableMultiSelectModal from "./ReusableMultiSelectModal";

const ReusableDataGrid = ({
  columns,
  rows,
  orgName,
  viewType,
  setCurrentPage,
  currentPage,
  totalRecords,
  isLoading,
  setQuery,
  query,
  checkboxSelection,
  tabDescription,
  syncStatus,
  setSyncStatus,
  refetchRequested,
  multiUserAction,
  enableHorizontalScroll = false,
  tableMinWidth,
  customSearchControls = null,
  customSearchInput = null,
}) => {
  const [queryData, setQueryData] = useState([]); // Store all users across pages
  const [isDataProcessingloading, setIsDataProcessingLoading] = useState(true);
  const [isSyncInitiatedByUser, setIsSyncInitiatedByUser] = useState(false);
  const [showMultiSelectModal, setShowMultiSelectModal] = useState(false);
  const [showBulkChangeBudgetModal, setShowBulkChangeBudgetModal] = useState(false);
  const [isSuccessMessageVisible, setIsSuccessMessageVisible] = useState(false);
  const [isSyncInProgress, setIsSyncInProgress] = useState(false);
  const [hasSyncRunStarted, setHasSyncRunStarted] = useState(false);
  const [isSyncModalOpen, setIsSyncModalOpen] = useState(false);
  const dispatch = useDispatch();
  const { orgId } = useParams();
  const userEmail = useSelector((state) => state.auth.userAccount?.username);
  const { data: platformData } = useGetPlatformsQuery();
  const [permissions, setPermissions] = useState([]);
  const userPermissions = useSelector((state) => state.auth.permissions);
  const isUserAuthorized =
    permissions?.userType === "Owner" || permissions?.userType === "Approver";

  // RTK Query for fetching sync task status
  const {
    data: orgUserSyncStatusData,
    isLoading: isOrgUserSyncStatusLoading,
    refetch: refetchOrgUserSyncStatus,
  } = useGetOrgUserSyncStatusQuery(
    { orgId },
    {
      skip: !orgId,
      pollingInterval: isSyncInProgress ? 15000 : 0,
    }
  );

  // Theme for tooltip
  const welcomeToolTipTheme = createTheme({
    components: {
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            fontSize: "0.9rem", // Adjust this value to your needs
            padding: "8px 12px",
          },
        },
      },
    },
  });

  //Functions to Handle Opening and Closing of Sync Modal
  const openSyncModal = () => {
    setIsSyncModalOpen(true);
  };

  const closeSyncModal = () => {
    setIsSyncModalOpen(false);
  };

  const syncTaskStatus = orgUserSyncStatusData?.status;
  const isSyncStatusInProgress = syncTaskStatus === "in_progress";

  // Trigger Sync Mutation
  const [
    triggerOrgUserSync,
    {
      isLoading: isTriggerOrgUserSyncLoading,
    },
  ] = useTriggerOrgUserSyncMutation();

  // Handle the Sync users button click
  const handleSyncClick = () => {
    setIsSyncInitiatedByUser(true);
    setHasSyncRunStarted(false);
    setIsSyncInProgress(true);
    closeSyncModal();
    triggerOrgUserSync({ orgId })
      .unwrap()
      .then((response) => {
        const triggerStatus = String(response?.status || "").toLowerCase();
        const startedStatuses = [
          "accepted",
          "queued",
          "starting",
          "in_progress",
          "already_running",
          "complete",
        ];

        if (startedStatuses.includes(triggerStatus)) {
          setHasSyncRunStarted(true);
        }

        refetchOrgUserSyncStatus();
      })
      .catch((error) => {
        setIsSyncInProgress(false);
        setIsSyncInitiatedByUser(false);
        setHasSyncRunStarted(false);
      });
  };

  // Determine Button State
  const isButtonDisabled = useMemo(() => {
    if (isOrgUserSyncStatusLoading || isTriggerOrgUserSyncLoading || isSyncInProgress) {
      return true;
    }

    return isSyncStatusInProgress;
  }, [
    isOrgUserSyncStatusLoading,
    isTriggerOrgUserSyncLoading,
    isSyncInProgress,
    isSyncStatusInProgress,
  ]);

  // Key for re-rendering the datagrid
  const dataGridKey = JSON.stringify(rows);

  // Custom locale text for datagrid empty rows
  const customLocaleText = {
    noRowsLabel: "No Users Found",
  };

  const [selectedRows, setSelectedRows] = useState([]);
  const [globalSelectedRows, setGlobalSelectedRows] = useState(new Set());

  // Sets selected rows to the selected rows in the datagrid
  const handleSelectionModelChange = (selection) => {
    setSelectedRows(selection);

    // Update global selection - simple approach
    const newGlobalSelection = new Set(globalSelectedRows);

    // Remove deselected items from current page
    currentRecords.forEach(row => {
      if (!selection.includes(row.id)) {
        newGlobalSelection.delete(row.id);
      }
    });

    // Add newly selected items
    selection.forEach(id => {
      newGlobalSelection.add(id);
    });

    setGlobalSelectedRows(newGlobalSelection);
  };

  const [recordsPerPage] = useState(25);

  // Search TextField styles
  const textStyles = styled({
    root: {
      "& .Mui-focused": {
        color: "#008080",
        outline: "1px solid  #008080",
      },
      "& .MuiDataGrid-columnHeaderTitle": {
        fontSize: "14px",
      },
    },
  });

  const [currentRecords, setCurrentRecords] = useState([]);

  useEffect(() => {
    if (userPermissions && orgId) {
      const orgPermissions = userPermissions.find(
        (permission) => permission.orgId === orgId
      );
      setPermissions(orgPermissions);
    }
  }, [userPermissions, orgId]);

  // Monitor sync task status and refresh all org-history tabs when a user-triggered sync completes.
  useEffect(() => {
    if (syncTaskStatus === "in_progress") {
      setIsSyncInProgress(true);
      if (isSyncInitiatedByUser) {
        setHasSyncRunStarted(true);
      }
      return;
    }

    if (syncTaskStatus === "error") {
      setIsSyncInProgress(false);
      if (isSyncInitiatedByUser) {
        setIsSyncInitiatedByUser(false);
        setHasSyncRunStarted(false);
      }
      return;
    }

    if (syncTaskStatus === "never_run") {
      // Idle state before any successful run is observed.
      if (!isSyncInitiatedByUser || !hasSyncRunStarted) {
        setIsSyncInProgress(false);
        if (isSyncInitiatedByUser) {
          setIsSyncInitiatedByUser(false);
          setHasSyncRunStarted(false);
        }
      }
      return;
    }

    if (syncTaskStatus === "complete") {
      setIsSyncInProgress(false);

      if (isSyncInitiatedByUser && hasSyncRunStarted) {
        dispatch(
          userServiceApiSlice.util.invalidateTags([
            { type: "User", id: "OrgRequests-Requested" },
            { type: "User", id: "OrgRequests-Joined" },
            { type: "User", id: "OrgRequests-Rejected" },
            { type: "User", id: "OrgRequests-Remove-Requested" },
            { type: "User", id: "OrgRequests-Removed" },
          ])
        );
        setIsSuccessMessageVisible(true);
        setIsSyncInitiatedByUser(false);
        setHasSyncRunStarted(false);
      }
    }
  }, [syncTaskStatus, isSyncInitiatedByUser, hasSyncRunStarted, dispatch]);

  useEffect(() => {
    if (rows) {
      // Use current page rows for display
      setCurrentRecords(rows);

      // Update selectedRows to reflect global selection for current page
      const currentPageIds = rows.map(row => row.id);
      const currentPageSelection = currentPageIds.filter(id => globalSelectedRows.has(id));
      setSelectedRows(currentPageSelection);
    }
  }, [currentPage, recordsPerPage, rows, globalSelectedRows]);

  const calcPages = useMemo(() => {
    return Math.ceil(totalRecords / recordsPerPage);
  }, [totalRecords]);

  const handleSearchChange = (event) => {
    setCurrentPage(1);
    setQuery(event.target.value);
  };

  const handleClose = (event, reason) => {
    if (reason === "backdropClick") {
    } else {
      setShowMultiSelectModal(false);
      setSelectedRows([]);
      setGlobalSelectedRows(new Set());
    }
  };

  const openModal = () => {
    setShowMultiSelectModal(true);
  };

  const openBulkChangeBudgetModal = () => {
    setShowBulkChangeBudgetModal(true);
  };

  const closeBulkChangeBudgetModal = () => {
    setShowBulkChangeBudgetModal(false);
    setSelectedRows([]);
    setGlobalSelectedRows(new Set());
  };

  // Batch update user org status for multi-select functionality
  const [
    batchUpdateUserOrgStatus,
    { data: batchUpdateData, isLoading: isBatchUpdateUserOrgStatusLoading },
  ] = useUpdateUserOrgStatusBatchMutation();

  const action = (platformId, orgId, userEmail) => {
    return (rejectReason, otherReason, rowData) => {
      batchUpdateUserOrgStatus({
        platformId: platformId,
        orgId: orgId,
        userEmail: userEmail,
        rejectReason: rejectReason,
        otherReason: otherReason,
        rowData: rowData,
      });
    };
  };

  useEffect(() => {
    if (isSuccessMessageVisible) {
      const timer = setTimeout(() => {
        setIsSuccessMessageVisible(false);
      }, 7000); // Auto close after 5 seconds

      return () => {
        clearTimeout(timer);
      };
    }
  }, [isSuccessMessageVisible]);

  useEffect(() => {
    if (rows) {
      // Accumulate all users from all pages into queryData
      setQueryData(prevData => {
        const existingIds = new Set(prevData.map(user => user.id));
        const newUsers = rows.filter(user => !existingIds.has(user.id));
        const totalUsers = [...prevData, ...newUsers];

        return totalUsers;
      });
      setIsDataProcessingLoading(false);
    }
  }, [rows]);

  const CustomFooter = () => {
    return (
      <GridFooterContainer>
        <GridFooterContainer sx={{ justifyContent: "flex-start" }}>
          {globalSelectedRows.size > 0 && (
            <div style={{ justifyContent: "flex-start" }}>
              {viewType === "Joined History" && (
                <Button
                  variant="contained"
                  sx={{
                    backgroundColor: "#26890d",
                    textTransform: "none",
                    letterSpacing: "0.5px",
                    padding: "2px 14px",
                    borderRadius: "10px",
                    fontSize: "12px",
                    color: "white",
                    marginRight: "5px",
                    "&:hover": {
                      backgroundColor: "#1e6b0a",
                      color: "white",
                    },
                  }}
                  onClick={() => openBulkChangeBudgetModal()}
                >
                  Change Budget ({globalSelectedRows.size})
                </Button>
              )}
              <Button
                variant="contained"
                sx={{
                  backgroundColor: "#fe0000",
                  textTransform: "none",
                  letterSpacing: "0.5px",
                  padding: "2px 14px",
                  borderRadius: "10px",
                  fontSize: "12px",
                  color: "white",
                  marginRight: "5px",
                  "&:hover": {
                    backgroundColor: "#fe0000",
                    color: "white",
                  },
                }}
                onClick={() => openModal()}
              >
                {viewType === "Joined History" ? "Revoke All" : "Reset All"} ({globalSelectedRows.size})
              </Button>
            </div>
          )}
          <GridFooter
            sx={{
              border: "none",
              display: "flex",
              justifyContent: "space-between",
            }}
          />
        </GridFooterContainer>
        <GridFooterContainer>
          <LargeDataPagination
            nPages={calcPages}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
          />
        </GridFooterContainer>
      </GridFooterContainer>
    );
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        marginBottom: "3rem",
      }}
    >
      {showMultiSelectModal && (
        <ReusableMultiSelectModal
          show={showMultiSelectModal}
          close={handleClose}
          selectedRows={Array.from(globalSelectedRows)}
          allRows={queryData}
          action={(rejectReason, otherReason) =>
            multiUserAction(Array.from(globalSelectedRows), rejectReason, otherReason)
          }
        />
      )}
      {showBulkChangeBudgetModal && (
        <BulkChangeBudgetModal
          show={showBulkChangeBudgetModal}
          close={closeBulkChangeBudgetModal}
          selectedUsers={Array.from(globalSelectedRows)
            .map((id) => queryData.find((row) => row.id === id))
            .filter(Boolean)}
          platformId={platformData?._id}
          approvedBy={userEmail}
          onRemoveUser={(userId) => {
            const updated = new Set(globalSelectedRows);
            updated.delete(userId);
            setGlobalSelectedRows(updated);
          }}
        />
      )}
      <SyncUsersConfirmationModal
        show={isSyncModalOpen}
        close={closeSyncModal}
        handleConfirmChange={handleSyncClick}
        OrgName={orgName}
      />
      <div
        style={{
          width: "100%",
          padding: "1rem",
          paddingTop: "0",
        }}
      >
        <div
          style={{
            fontWeight: "600",
            fontSize: "18px",
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "space-between",
            width: "100%",
          }}
        >
          <div
            style={{
              fontWeight: "600",
              fontSize: "18px",
              display: "flex",
              flexDirection: "column",
              width: "60%",
              marginBottom: "0.5rem",
            }}
          >
            {orgName || ""}
            <Typography variant="subtitle1">Org History</Typography>
          </div>
          {/* tabDescription.includes("Users in Requested") &&  */}

          {/* Sync Button - Show only for Requested */}
          <ThemeProvider theme={welcomeToolTipTheme}>
            {isUserAuthorized &&
              (
                <Tooltip
                  placement="right"
                  title={
                    <div>
                      {
                        "This action syncs the user's membership status in our database with the latest available information from GitHub."
                      }
                      <br />
                      {
                        "If users are stuck in Requested state for more than 2 hours, please raise an ECR support request."
                      }
                    </div>
                  }
                  sx={{
                    marginLeft: "5px",
                    color: "#008080",
                    cursor: "pointer",
                  }}
                >
                  <div style={{ display: "inline-block", minWidth: "8rem" }}>
                    <LoadingButton
                      text={"Sync Users"}
                      variant={"contained"}
                      backgroundColor={"#86BC25"}
                      color={"#fff"}
                      cursor={
                        isSyncStatusInProgress
                          ? "disabled"
                          : "pointer"
                      }
                      onClickFunc={openSyncModal}
                      loadingState={
                        isSyncInProgress ||
                        isTriggerOrgUserSyncLoading ||
                        isSyncStatusInProgress
                      }
                      loadingText={
                        isSyncInProgress ||
                          isSyncStatusInProgress
                          ? "In Progress"
                          : "Syncing..."
                      }
                      disabled={
                        isButtonDisabled ||
                        isTriggerOrgUserSyncLoading
                        // ||
                        // rows.length === 0
                      }
                    />
                  </div>
                </Tooltip>
              )}
          </ThemeProvider>
          {/* Sync success or progress message */}
          {(isSyncInProgress ||
            isTriggerOrgUserSyncLoading ||
            isSuccessMessageVisible ||
            isSyncStatusInProgress) && (
              <Box
                sx={{
                  // mt: 1.5,
                  p: 0.8,
                  backgroundColor:
                    isSyncInProgress ||
                      isTriggerOrgUserSyncLoading ||
                      isSyncStatusInProgress
                      ? "#fff3cd"
                      : "#e8f5e9",
                  borderRadius: "8px",
                  border: "1px solid",
                  borderColor:
                    isSyncInProgress ||
                      isTriggerOrgUserSyncLoading ||
                      isSyncStatusInProgress
                      ? "#ffeeba"
                      : "#b9f6ca",
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                {/* Show clock icon when sync is in progress and Show success tick icon when sync is successful */}

                {isSyncInProgress ||
                  isTriggerOrgUserSyncLoading ||
                  isSyncStatusInProgress ? (
                  <Zoom in={true} style={{ transitionDelay: "100ms" }}>
                    <AccessTimeIcon
                      sx={{
                        color: "#856404",
                      }}
                    />
                  </Zoom>
                ) : isSuccessMessageVisible ? (
                  <Zoom in={true} style={{ transitionDelay: "100ms" }}>
                    <TaskAltIcon
                      sx={{
                        color: "#2e7d32",
                      }}
                    />
                  </Zoom>
                ) : null}
                <Typography
                  variant="subtitle2"
                  sx={{
                    color:
                      isSyncInProgress ||
                        isTriggerOrgUserSyncLoading ||
                        isSyncStatusInProgress
                        ? "#856404"
                        : "#2e7d32",
                    fontWeight: "medium",
                  }}
                >
                  {isSyncInProgress ||
                    isTriggerOrgUserSyncLoading ||
                    isSyncStatusInProgress
                    ? "Sync In Progress, please wait.."
                    : "Success! Users Synced"}
                </Typography>
              </Box>
            )}
        </div>
        <hr style={{ backgroundColor: "#d0d7de", width: "100%" }}></hr>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: "2rem",
          }}
        >
          <span
            style={{
              fontSize: "14px",
              fontWeight: "400",
              color: "#808080",
              lineHeight: "1.5",
              marginTop: "0.5rem",
              width: "60%",
            }}
          >
            {tabDescription}
          </span>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
            {customSearchControls}
            {/* Dynamic Search/Filter Input or Default Search */}
            {customSearchInput || (
              <TextField
                id="outlined-search"
                placeholder="Search Users By Email"
                width="100%"
                type="search"
                className={textStyles.root}
                variant="outlined"
                InputProps={{
                  style: {
                    height: "31px",
                  },
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                onChange={handleSearchChange}
              />
            )}
          </Box>
        </div>
      </div>
      <div
        style={{
          height: "600px",
          width: "100%",
          overflowX: enableHorizontalScroll ? "auto" : "hidden",
        }}
      >
        <DataGrid
          sx={{
            minWidth: tableMinWidth || "100%",
            "& .MuiDataGrid-columnHeaderTitle": {
              fontSize: "13px !important",
              fontWeight: "normal !important",
            },
            "& .MuiDataGrid-columnHeader": {
              backgroundColor: "#f6f6f6",
              maxHeight: "45px",
              fontSize: "12px !important",
            },
            "& .MuiDataGrid-row": {
              minHeight: "60px !important",
              boxShadow: "none !important",
              borderBottom: "1px solid #d4d4d4",
            },
            "& .MuiDataGrid-cell": {
              fontSize: "14px !important",
            },
            "& .MuiDataGrid-virtualScroller": {
              overflowX: enableHorizontalScroll ? "auto" : "hidden",
            },
          }}
          components={{ Footer: CustomFooter }}
          key={dataGridKey}
          getRowId={(row) => row.id}
          rows={currentRecords}
          columns={columns}
          disableVirtualization
          disableSelectionOnClick
          disableColumnMenu={true} // Disable three-dot menu on column headers
          checkboxSelection={checkboxSelection}
          localeText={customLocaleText}
          onSelectionModelChange={handleSelectionModelChange}
          selectionModel={selectedRows}
          isRowSelectable={(params) =>
            params.row.status === "joined" ||
            params.row.status === "rejected" ||
            params.row.status === "removed"
          }
          loadingOverlayClassName="custom-loading-indicator"
          loading={
            isLoading ||
            isDataProcessingloading ||
            isBatchUpdateUserOrgStatusLoading
          }
        />
      </div>
    </div>
  );
};

export default ReusableDataGrid;
